import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Section from "../Settings/Section";
import { SeverityIcon } from "../../common/AlertSeverity";
import { useAuth } from "../../../contexts/AuthContext";
import { useVisibleLoading } from "../../../utils/useVisibleLoading";
import {
  clearAlertEvents,
  getAlertEvents,
  markAlertEventRead,
  markAlertEventUnread,
  markAllAlertEventsRead,
  type AlertEvent,
} from "../../../api/alerts";

/**
 * What has actually fired, split into what still wants attention and what has
 * been dealt with. "Archived" is the same state the notification center calls
 * read — acknowledging in either place clears it in both, and for everyone.
 */

const REFRESH_MS = 60_000;
const LIMIT = 100;

/** Pull the API's `detail` out of an axios error, with a sane fallback. */
function errText(e: any, fallback: string): string {
  const detail = e?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
}

/** "12s ago" / "3m ago" / "5h ago" / "2d ago" from a seconds count. */
function humanizeAgo(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** Nashville-local naive ISO → readable local time, for the hover tooltip. */
function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const AlertsFeed: React.FC = () => {
  const { hasAdminAccess } = useAuth();
  const [events, setEvents] = useState<AlertEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmArchiveAll, setConfirmArchiveAll] = useState(false);
  // Archived is history: collapsed on arrival so what needs attention is what
  // you see. Deliberately not sticky — each visit starts on the current list.
  const [showArchived, setShowArchived] = useState(false);

  const [snack, setSnack] = useState<{
    open: boolean;
    msg: string;
    severity: "success" | "error";
  }>({ open: false, msg: "", severity: "success" });

  const showSnack = (msg: string, severity: "success" | "error") =>
    setSnack({ open: true, msg, severity });

  // Bumped by every archive/unarchive. A fetch that started before a change
  // carries pre-change state, so its response is dropped rather than merged:
  // archiving and un-archiving move in opposite directions, and no merge rule
  // can tell "this row is still unread" from "you just un-archived it".
  const mutations = useRef(0);

  const load = useCallback(async (signal?: AbortSignal) => {
    const startedAt = mutations.current;
    try {
      const fetched = await getAlertEvents({ limit: LIMIT }, signal);
      if (mutations.current !== startedAt) return; // stale; local state is newer
      setEvents(fetched);
      setFailed(false);
    } catch (e: any) {
      if (e?.name !== "CanceledError") setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    const id = setInterval(() => load(), REFRESH_MS);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [load]);

  // Held briefly so the wheel is visible even when the API answers instantly.
  const showLoading = useVisibleLoading(loading);

  const { active, archived } = useMemo(
    () => ({
      active: events.filter((e) => !e.read),
      archived: events.filter((e) => e.read),
    }),
    [events]
  );

  /** Optimistic: the row moves section at once, the server catches up. */
  const setRead = async (e: AlertEvent, read: boolean) => {
    mutations.current += 1;
    setEvents((prev) =>
      prev.map((x) => (x.id === e.id ? { ...x, read } : x))
    );
    try {
      await (read ? markAlertEventRead(e.id) : markAlertEventUnread(e.id));
    } catch (err: any) {
      showSnack(
        errText(err, read ? "Could not archive alert" : "Could not restore alert"),
        "error"
      );
      load();
    }
  };

  const archiveAll = async () => {
    setConfirmArchiveAll(false);
    mutations.current += 1;
    setEvents((prev) => prev.map((e) => ({ ...e, read: true })));
    try {
      await markAllAlertEventsRead();
    } catch (err: any) {
      showSnack(errText(err, "Could not archive alerts"), "error");
      load();
    }
  };

  const clearAll = async () => {
    const previous = events;
    setConfirmClear(false);
    setEvents([]);
    try {
      await clearAlertEvents();
      showSnack("Alert history cleared", "success");
    } catch (err: any) {
      setEvents(previous);
      showSnack(errText(err, "Could not clear history"), "error");
    }
  };

  const renderRows = (rows: AlertEvent[], archivedRows: boolean) => (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small">
        <TableBody>
          {rows.map((e) => (
            <TableRow key={e.id} hover sx={{ opacity: archivedRows ? 0.65 : 1 }}>
              <TableCell sx={{ width: 36, pr: 0 }}>
                <SeverityIcon severity={e.severity} />
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: archivedRows ? 400 : 600 }}
                >
                  {e.message}
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ mt: 0.25 }}
                >
                  {/* Type labels repeat across areas, so the area is what
                      tells you where this fired. */}
                  <Typography variant="caption" color="text.secondary">
                    {e.area_label} · {e.type_label}
                  </Typography>
                  {/* Seeded sample rows, until a dispatcher fires real ones. */}
                  {e.is_demo && (
                    <Chip
                      label="Demo"
                      size="small"
                      variant="outlined"
                      sx={{ height: 18, fontSize: 10 }}
                    />
                  )}
                </Stack>
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                <Tooltip title={formatWhen(e.created_at)}>
                  <Typography variant="caption" color="text.secondary">
                    {humanizeAgo(e.age_seconds)}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell align="right" sx={{ width: 48 }}>
                {archivedRows ? (
                  <Tooltip title="Mark unread">
                    <IconButton
                      size="small"
                      aria-label={`Mark unread: ${e.message}`}
                      onClick={() => setRead(e, false)}
                    >
                      <UnarchiveOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Archive">
                    <IconButton
                      size="small"
                      aria-label={`Archive: ${e.message}`}
                      onClick={() => setRead(e, true)}
                    >
                      <ArchiveOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (showLoading) {
    return (
      <Box
        aria-busy="true"
        sx={{ display: "flex", justifyContent: "center", py: 8 }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    // Full width of the tab: these are wide rows — message, type, age, action
    // — and capping them at a reading column wastes the screen.
    <Box sx={{ width: "100%", py: { xs: 1, sm: 2 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Alerts that have fired. Archiving one clears it here and in the
          notification bell, for everyone.
        </Typography>
        <Tooltip title="Refresh">
          <IconButton size="small" aria-label="Refresh alerts" onClick={() => load()}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {failed && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load alerts.
        </Alert>
      )}

      <Section
        title={`Current${active.length ? ` (${active.length})` : ""}`}
        subtitle="Fired and not yet acknowledged"
        rightAction={
          <Button
            size="small"
            variant="outlined"
            startIcon={<ArchiveOutlinedIcon />}
            onClick={() => setConfirmArchiveAll(true)}
            disabled={!active.length}
          >
            Archive All
          </Button>
        }
      >
        {active.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing active — every alert has been acknowledged.
          </Typography>
        ) : (
          renderRows(active, false)
        )}
      </Section>

      <Section
        title={`Archived${archived.length ? ` (${archived.length})` : ""}`}
        subtitle="Acknowledged, kept for history"
        rightAction={
          <Stack direction="row" spacing={1}>
            {archived.length > 0 && (
              <Button
                size="small"
                onClick={() => setShowArchived((v) => !v)}
                startIcon={showArchived ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                aria-expanded={showArchived}
              >
                {showArchived ? "Hide" : `Show (${archived.length})`}
              </Button>
            )}
            {/* Clearing deletes history for everyone, so admins and managers only. */}
            {hasAdminAccess && (
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<DeleteSweepIcon />}
                onClick={() => setConfirmClear(true)}
                disabled={!events.length}
              >
                Clear History
              </Button>
            )}
          </Stack>
        }
      >
        {archived.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing archived yet.
          </Typography>
        ) : (
          // unmountOnExit: collapsed history should not sit in the DOM, so
          // "what is on screen" and "what exists" stay the same thing.
          <Collapse in={showArchived} unmountOnExit>
            {renderRows(archived, true)}
          </Collapse>
        )}
      </Section>

      <Dialog
        open={confirmArchiveAll}
        onClose={() => setConfirmArchiveAll(false)}
        maxWidth="xs"
      >
        <DialogTitle>Archive All Alerts</DialogTitle>
        <DialogContent>
          <Typography>
            Archive {active.length} current alert
            {active.length === 1 ? "" : "s"}? They move to history and clear
            from the notification bell for everyone. You can mark any of them
            unread afterwards.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmArchiveAll(false)}>Cancel</Button>
          <Button variant="contained" onClick={archiveAll}>
            Archive All
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmClear} onClose={() => setConfirmClear(false)} maxWidth="xs">
        <DialogTitle>Clear Alert History</DialogTitle>
        <DialogContent>
          <Typography>
            This deletes all {events.length} alert
            {events.length === 1 ? "" : "s"}, current and archived, for everyone.
            It cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClear(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={clearAll}>
            Clear History
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AlertsFeed;
